import { useEffect } from "react";

import { isEmpty } from "lodash";

import { useGetManagerProfile } from "manager/hooks/api";
import { useActiveUser } from "shared/hooks/api";
import { isManagerApp, useClaims } from "shared/utils/auth";
import {
  buildCustomerioIdentifyFingerprint,
  getLastCustomerioIdentifyFingerprint,
  identifyCustomerio,
  setLastCustomerioIdentifyFingerprint,
} from "shared/utils/customerio";

import { buildCustomerioIdentifyAttributes } from "./customerioIdentify";

type UseCustomerioIdentifyOptions = {
  enabled: boolean;
  snippetReady: boolean;
};

/**
 * Identifies the logged-in manager in Customer.io once snippet + profile are ready.
 * HMAC `userHash` comes from backend `/user/profile/` (PRODUCT-1685), not person-service.
 */
export const useCustomerioIdentify = ({
  enabled,
  snippetReady,
}: UseCustomerioIdentifyOptions): void => {
  const { claims, company } = useClaims();
  const { activeUser, isActiveUserLoading } = useActiveUser();
  const { data: profile, isFetched: isProfileFetched } = useGetManagerProfile({
    enabled:
      enabled &&
      snippetReady &&
      !isEmpty(company) &&
      !isEmpty(activeUser) &&
      isManagerApp(),
  });

  const profileReady =
    !isEmpty(company) &&
    !isEmpty(activeUser) &&
    !isActiveUserLoading &&
    isProfileFetched &&
    Boolean(claims?.sub);

  const identifyReady =
    enabled && snippetReady && isManagerApp() && profileReady;

  useEffect(() => {
    if (!identifyReady) return;

    const attrs = buildCustomerioIdentifyAttributes({
      claims,
      activeUser,
      company,
      managerRole: profile?.role,
    });

    if (!attrs) return;

    const fingerprint = buildCustomerioIdentifyFingerprint(attrs);
    if (getLastCustomerioIdentifyFingerprint() === fingerprint) return;

    identifyCustomerio(attrs);
    setLastCustomerioIdentifyFingerprint(fingerprint);
  }, [
    identifyReady,
    claims?.sub,
    claims?.email,
    claims?.givenName,
    claims?.familyName,
    claims?.createdAt,
    activeUser?.email,
    activeUser?.firstName,
    activeUser?.lastName,
    activeUser?.customerioHash,
    activeUser?.createdAt,
    company?.slug,
    company?.name,
    profile?.role,
  ]);
};
